import React, { useEffect, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, CheckCircle2Icon, HomeIcon, MapPinIcon, LoaderIcon, PhoneIcon } from 'lucide-react';
interface KohlerQuoteFormProps {
  initialZip: string;
  productSlug: string;
  onClose: () => void;
}
export function KohlerQuoteForm({
  initialZip,
  productSlug,
  onClose
}: KohlerQuoteFormProps) {
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [zipLookupLoading, setZipLookupLoading] = useState(false);
  const [zipLookupDone, setZipLookupDone] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [editingZip, setEditingZip] = useState(false);
  const isShower = productSlug === 'showers';
  const brandColor = isShower ? '#C5D93D' : '#C5A55A';
  const brandDark = isShower ? '#9EAE31' : '#9E8448';
  const [formData, setFormData] = useState({
    BDMLeadType: 'test',
    alid: '2772',
    oid: '10059',
    campaignnumber_koh: '8008393839',
    businessgroup_koh: isShower ? 'Shower' : 'Walk-In Bath',
    websource_koh: 'findservicequotes.io',
    trustedform_cert_url: '',
    first_name: '',
    last_name: '',
    phone_1: '',
    email: '',
    address_1: '',
    city: '',
    state: '',
    postal_code: initialZip || '',
    project_details: '',
    homeOwnership: '',
    features_needed: '',
    purchasing_for: ''
  });
  // Zip code lookup
  useEffect(() => {
    const zip = formData.postal_code;
    if (zip && zip.length === 5) {
      setZipLookupLoading(true);
      setZipLookupDone(false);
      fetch(`https://api.zippopotam.us/us/${zip}`).then(res => {
        if (!res.ok) throw new Error('Invalid zip');
        return res.json();
      }).then(data => {
        if (data.places && data.places.length > 0) {
          const place = data.places[0];
          setFormData(prev => ({
            ...prev,
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          }));
          setZipLookupDone(true);
        }
      }).catch(() => {
        setZipLookupDone(false);
      }).finally(() => {
        setZipLookupLoading(false);
      });
    }
  }, [formData.postal_code]);
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
    setFormData(prev => ({
      ...prev,
      phone_1: digits
    }));
  };
  // TrustedForm
  useEffect(() => {
    try {
      const tf = document.createElement('script');
      tf.type = 'text/javascript';
      tf.async = true;
      tf.src = 'https://api.trustedform.com/trustedform.js?provide_referrer=false&field=xxTrustedFormCertUrl&l=' + new Date().getTime() + Math.random();
      document.head.appendChild(tf);
    } catch (e) {
      console.warn('TrustedForm script could not be loaded:', e);
    }
    const interval = setInterval(() => {
      try {
        const certInput = document.querySelector('input[name="xxTrustedFormCertUrl"]') as HTMLInputElement;
        if (certInput && certInput.value) {
          setFormData(prev => ({
            ...prev,
            trustedform_cert_url: certInput.value
          }));
          clearInterval(interval);
        }
      } catch (e) {
        // continue polling
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const totalSteps = isShower ? 4 : 5;
  const progress = step / totalSteps * 100;
  const handleNext = () => {
    setDirection(1);
    setStep(prev => Math.min(prev + 1, totalSteps));
  };
  const handleBack = () => {
    setDirection(-1);
    setStep(prev => Math.max(prev - 1, 1));
  };
  const handleOptionSelect = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setTimeout(() => handleNext(), 300);
  };
  const validateAddress = (address: string): boolean => {
    const addressPattern = /^\d+\s+\S{2,}/;
    return addressPattern.test(address.trim());
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (name === 'address_1') {
      setAddressError('');
    }
  };
  const handleAddressBlur = () => {
    if (formData.address_1 && !validateAddress(formData.address_1)) {
      setAddressError('Please enter a valid street address (e.g. 123 Main St)');
    } else {
      setAddressError('');
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const certInput = document.querySelector('input[name="xxTrustedFormCertUrl"]') as HTMLInputElement;
    const finalCert = certInput?.value || formData.trustedform_cert_url;
    // Submit to BDM lead management
    const bdmParams = new URLSearchParams();
    bdmParams.append('BDMLeadType', formData.BDMLeadType);
    bdmParams.append('alid', formData.alid);
    bdmParams.append('oid', formData.oid);
    bdmParams.append('campaignnumber_koh', formData.campaignnumber_koh);
    bdmParams.append('businessgroup_koh', formData.businessgroup_koh);
    bdmParams.append('websource_koh', formData.websource_koh);
    bdmParams.append('first_name', formData.first_name);
    bdmParams.append('last_name', formData.last_name);
    bdmParams.append('phone_1', formData.phone_1);
    bdmParams.append('email', formData.email);
    bdmParams.append('address_1', formData.address_1);
    bdmParams.append('city', formData.city);
    bdmParams.append('state', formData.state);
    bdmParams.append('postal_code', formData.postal_code);
    bdmParams.append('trustedform_cert_url', finalCert);
    // Add custom fields as notes or extra params if needed
    if (formData.features_needed) bdmParams.append('features_needed', formData.features_needed);
    if (formData.purchasing_for) bdmParams.append('purchasing_for', formData.purchasing_for);
    if (formData.project_details) bdmParams.append('project_details', formData.project_details);
    const bdmUrl = `http://bdmleadmanagement.valid1.net/Leads.aspx?${bdmParams.toString()}`;
    console.log('Submitting to BDM:', bdmUrl);
    try {
      await fetch(bdmUrl, {
        method: 'POST',
        mode: 'no-cors'
      });
    } catch (err) {
      console.warn('BDM post failed, continuing:', err);
    }
    setIsSubmitting(false);
    setIsSuccess(true);
  };
  const variants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (dir: number) => ({
      x: dir < 0 ? 50 : -50,
      opacity: 0
    })
  };
  const KohlerHeader = ({
    hideExit = false
  }: {
    hideExit?: boolean;
  }) => <header className="bg-black border-b border-gray-800 py-4 px-6 flex justify-between items-center sticky top-0 z-10" data-id="element-1998">
      <div className="flex flex-col" data-id="element-1999">
        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-[-4px]" data-id="element-2000">
          Partner
        </span>
        <div className="flex items-baseline gap-2" data-id="element-2001">
          <span className="text-2xl md:text-3xl font-black text-white tracking-tight" data-id="element-2002">
            KOHLER
          </span>
        </div>
        <span className="text-[10px] md:text-xs font-semibold text-gray-300" data-id="element-2003">
          {isShower ? 'Showers' : 'Walk-In Baths'} Estimate
        </span>
      </div>
      <div className="flex items-center gap-6" data-id="element-2004">
        {!hideExit && <button onClick={onClose} className="text-sm font-medium text-gray-400 hover:text-white transition-colors flex items-center gap-1" data-id="element-2005">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-2006" />
            <span className="hidden sm:inline" data-id="element-2007">Exit</span>
          </button>}
      </div>
    </header>;
  if (isSuccess) {
    return <div className="min-h-screen bg-white flex flex-col font-sans" data-id="element-2008">
        <KohlerHeader hideExit data-id="element-2009" />

        {/* Hero Banner */}
        <div className="relative w-full h-64 md:h-80 flex items-center" data-id="element-2010">
          <div className="absolute inset-0 z-0" data-id="element-2011">
            <img src={isShower ? 'https://images.unsplash.com/photo-1620626011761-996317b8d101?w=1920&q=80' : 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=1920&q=80'} alt="Kohler bathroom" className="w-full h-full object-cover" data-id="element-2012" />
          </div>
          <div className="absolute inset-0 z-10" data-id="element-2013">
            <div className="absolute left-0 top-0 bottom-0 w-full md:w-2/3 bg-black/80 flex flex-col justify-center px-8 md:px-16" data-id="element-2014">
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight" data-id="element-2015">
                Your Quote Request Has
                <br data-id="element-2016" />
                Been Submitted!
              </h1>
              <p className="text-lg md:text-xl text-white/90" data-id="element-2017">
                A KOHLER representative will be calling you shortly.
              </p>
            </div>
          </div>
        </div>

        {/* Keep Your Phone Nearby Section */}
        <div className="max-w-3xl mx-auto w-full px-6 py-16 text-center" data-id="element-2018">
          <div className="flex justify-center mb-6" data-id="element-2019">
            <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{
            backgroundColor: `${brandColor}20`
          }} data-id="element-2020">
              <PhoneIcon className="w-10 h-10" style={{
              color: brandDark
            }} data-id="element-2021" />
            </div>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6" data-id="element-2022">
            Keep your phone nearby!
          </h2>
          <p className="text-gray-600 leading-relaxed max-w-xl mx-auto" data-id="element-2023">
            A KOHLER representative will be reaching out to you at{' '}
            <span className="font-semibold text-gray-900" data-id="element-2024">
              {formData.phone_1}
            </span>{' '}
            to discuss your {isShower ? 'shower' : 'walk-in bath'} project and
            schedule your free in-home estimate.
          </p>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-[#FAFAFA] flex flex-col" data-id="element-2025">
      <KohlerHeader data-id="element-2026" />
      <input type="hidden" name="xxTrustedFormCertUrl" data-id="element-2027" />

      {/* Progress Bar */}
      <div className="w-full bg-gray-200 h-2" data-id="element-2028">
        <motion.div className="h-full" style={{
        backgroundColor: brandDark
      }} initial={{
        width: 0
      }} animate={{
        width: `${progress}%`
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} data-id="element-2029" />
      </div>

      <main className="flex-1 flex flex-col items-center pt-8 pb-20 px-4" data-id="element-2030">
        <div className="w-full max-w-2xl" data-id="element-2031">
          <div className="mb-6 h-8" data-id="element-2032">
            {step > 1 && <button onClick={handleBack} className="text-gray-500 hover:text-black font-medium flex items-center gap-2 transition-colors" data-id="element-2033">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-2034" /> Back
              </button>}
          </div>

          <div className="relative bg-white rounded-2xl shadow-lg p-6 md:p-10 border border-gray-100" data-id="element-2035">
            <AnimatePresence mode="wait" custom={direction} data-id="element-2036">
              {/* SHOWER FLOW: Step 1 */}
              {isShower && step === 1 && <motion.div key="shower-step1" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-2037">
                  <h1 className="text-2xl md:text-3xl font-bold text-black mb-8 text-center" data-id="element-2038">
                    What type of shower project are you looking for?
                  </h1>
                  <div className="grid grid-cols-1 gap-4" data-id="element-2039">
                    {[{
                  value: 'New Installation',
                  label: 'New Shower Installation'
                }, {
                  value: 'Replacement',
                  label: 'Shower Replacement'
                }, {
                  value: 'Tub to Shower',
                  label: 'Tub-to-Shower Conversion'
                }].map((opt, idx) => <button key={idx} onClick={() => handleOptionSelect('project_details', opt.value)} className={`p-6 rounded-xl border-2 text-center font-bold text-lg transition-all duration-200 ${formData.project_details === opt.value ? 'border-black bg-gray-50 text-black shadow-md' : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:shadow-sm'}`} data-id="element-2040">
                        {opt.label}
                      </button>)}
                  </div>
                </motion.div>}

              {/* WALK-IN BATH FLOW: Step 1 */}
              {!isShower && step === 1 && <motion.div key="bath-step1" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-2041">
                  <h1 className="text-2xl md:text-3xl font-bold text-black mb-8 text-center" data-id="element-2042">
                    Which features are most important to you?
                  </h1>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2043">
                    {[{
                  value: 'Whirlpool jets',
                  label: 'Whirlpool jets'
                }, {
                  value: 'Low 3" step',
                  label: 'Low 3" step'
                }, {
                  value: 'Shower package',
                  label: 'Shower package'
                }, {
                  value: 'Wide door',
                  label: 'Wide door'
                }, {
                  value: 'Not Sure',
                  label: 'Not Sure'
                }].map((opt, idx) => <button key={idx} onClick={() => handleOptionSelect('features_needed', opt.value)} className={`p-6 rounded-xl border-2 text-center font-bold text-lg transition-all duration-200 ${formData.features_needed === opt.value ? 'border-black bg-gray-50 text-black shadow-md' : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:shadow-sm'}`} data-id="element-2044">
                        {opt.label}
                      </button>)}
                  </div>
                </motion.div>}

              {/* WALK-IN BATH FLOW: Step 2 */}
              {!isShower && step === 2 && <motion.div key="bath-step2" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-2045">
                  <h1 className="text-2xl md:text-3xl font-bold text-black mb-8 text-center" data-id="element-2046">
                    Who are you purchasing a Walk-In Bath for?
                  </h1>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2047">
                    {[{
                  value: 'Self',
                  label: 'Self'
                }, {
                  value: 'Spouse',
                  label: 'Spouse'
                }, {
                  value: 'Parent',
                  label: 'Parent'
                }, {
                  value: 'Other',
                  label: 'Other'
                }].map((opt, idx) => <button key={idx} onClick={() => handleOptionSelect('purchasing_for', opt.value)} className={`p-6 rounded-xl border-2 text-center font-bold text-lg transition-all duration-200 ${formData.purchasing_for === opt.value ? 'border-black bg-gray-50 text-black shadow-md' : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:shadow-sm'}`} data-id="element-2048">
                        {opt.label}
                      </button>)}
                  </div>
                </motion.div>}

              {/* COMMON STEP: Homeownership */}
              {(isShower && step === 2 || !isShower && step === 3) && <motion.div key="homeownership" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-2049">
                  <h1 className="text-2xl md:text-3xl font-bold text-black mb-8 text-center" data-id="element-2050">
                    Do you own your home?
                  </h1>
                  <div className="flex flex-col gap-4" data-id="element-2051">
                    {[{
                  value: 'Yes',
                  desc: 'I own this property'
                }, {
                  value: 'No',
                  desc: 'I rent this property'
                }].map((option, idx) => <button key={idx} onClick={() => handleOptionSelect('homeOwnership', option.value)} className={`p-5 rounded-xl border-2 text-left flex items-center gap-4 transition-all duration-200 ${formData.homeOwnership === option.value ? 'border-black bg-gray-50 shadow-md' : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'}`} data-id="element-2052">
                        <div className={`p-3 rounded-full flex-shrink-0 ${formData.homeOwnership === option.value ? 'bg-gray-200 text-black' : 'bg-gray-50 text-gray-500'}`} data-id="element-2053">
                          <HomeIcon className="w-6 h-6" data-id="element-2054" />
                        </div>
                        <div data-id="element-2055">
                          <div className="font-bold text-gray-900 text-lg" data-id="element-2056">
                            {option.value}
                          </div>
                          <div className="text-gray-500 text-sm" data-id="element-2057">
                            {option.desc}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </motion.div>}

              {/* COMMON STEP: Address */}
              {(isShower && step === 3 || !isShower && step === 4) && <motion.div key="address" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-2058">
                  <h1 className="text-2xl md:text-3xl font-bold text-black mb-8 text-center" data-id="element-2059">
                    Where is your project located?
                  </h1>

                  <div className="space-y-5" data-id="element-2060">
                    <div data-id="element-2061">
                      <label htmlFor="address_1" className="block text-sm font-bold text-gray-700 mb-1" data-id="element-2062">
                        Street Address
                      </label>
                      <input type="text" id="address_1" name="address_1" value={formData.address_1} onChange={handleInputChange} onBlur={handleAddressBlur} className={`w-full h-14 px-4 bg-gray-50 border rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent ${addressError ? 'border-red-400 bg-red-50' : 'border-gray-300'}`} placeholder="123 Main St" data-id="element-2063" />
                      {addressError && <p className="text-red-500 text-xs mt-1.5 font-medium" data-id="element-2064">
                          {addressError}
                        </p>}

                      <div className="mt-3 flex flex-col gap-2" data-id="element-2065">
                        {zipLookupLoading ? <div className="flex items-center gap-2 text-gray-400 text-sm" data-id="element-2066">
                            <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-2067" />
                            <span data-id="element-2068">Looking up location...</span>
                          </div> : editingZip ? <div className="flex items-center gap-2 w-full" data-id="element-2069">
                            <MapPinIcon className="w-4 h-4 text-black flex-shrink-0" data-id="element-2070" />
                            <input type="text" value={formData.postal_code} onChange={e => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                        setFormData(prev => ({
                          ...prev,
                          postal_code: val,
                          city: '',
                          state: ''
                        }));
                        setZipLookupDone(false);
                      }} onBlur={() => {
                        if (formData.postal_code.length === 5) setEditingZip(false);
                      }} onKeyDown={e => {
                        if (e.key === 'Enter' && formData.postal_code.length === 5) setEditingZip(false);
                      }} placeholder="Enter ZIP" maxLength={5} autoFocus className="flex-1 h-10 px-3 bg-white border border-gray-300 rounded-lg text-sm font-semibold text-black focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent" data-id="element-2071" />
                          </div> : zipLookupDone && formData.city && formData.state ? <button type="button" onClick={() => setEditingZip(true)} className="flex items-center gap-2 border rounded-lg px-3 py-2 w-full transition-colors cursor-pointer text-left bg-gray-50 border-gray-200 hover:bg-gray-100" data-id="element-2072">
                            <MapPinIcon className="w-4 h-4 flex-shrink-0 text-black" data-id="element-2073" />
                            <span className="text-sm font-semibold text-black" data-id="element-2074">
                              {formData.city}, {formData.state}{' '}
                              {formData.postal_code}
                            </span>
                            <span className="text-[10px] text-gray-400 ml-auto" data-id="element-2075">
                              edit
                            </span>
                          </button> : <button type="button" onClick={() => setEditingZip(true)} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 w-full hover:bg-gray-100 transition-colors cursor-pointer text-left" data-id="element-2076">
                            <MapPinIcon className="w-4 h-4 text-gray-400" data-id="element-2077" />
                            <span className="text-sm text-gray-500" data-id="element-2078">
                              {formData.postal_code ? `Zip: ${formData.postal_code}` : 'Enter your ZIP code'}
                            </span>
                          </button>}
                      </div>
                    </div>

                    <button onClick={() => {
                  if (!validateAddress(formData.address_1)) {
                    setAddressError('Please enter a valid street address (e.g. 123 Main St)');
                    return;
                  }
                  handleNext();
                }} disabled={!formData.address_1 || !formData.city || !formData.state || !formData.postal_code || !!addressError} className="w-full mt-6 h-14 bg-black hover:bg-gray-800 text-white font-bold text-lg rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2079">
                      Continue
                    </button>
                  </div>
                </motion.div>}

              {/* COMMON STEP: Contact Information */}
              {(isShower && step === 4 || !isShower && step === 5) && <motion.div key="contact" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="w-full" data-id="element-2080">
                  <h1 className="text-2xl md:text-3xl font-bold text-black mb-2 text-center" data-id="element-2081">
                    Who should we send the quote to?
                  </h1>
                  <p className="text-gray-500 text-center mb-8" data-id="element-2082">
                    Last step! Your information is secure.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-5" data-id="element-2083">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-2084">
                      <div data-id="element-2085">
                        <label htmlFor="first_name" className="block text-sm font-bold text-gray-700 mb-1" data-id="element-2086">
                          First Name
                        </label>
                        <input type="text" id="first_name" name="first_name" required value={formData.first_name} onChange={handleInputChange} className="w-full h-14 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent" placeholder="First Name" data-id="element-2087" />
                      </div>
                      <div data-id="element-2088">
                        <label htmlFor="last_name" className="block text-sm font-bold text-gray-700 mb-1" data-id="element-2089">
                          Last Name
                        </label>
                        <input type="text" id="last_name" name="last_name" required value={formData.last_name} onChange={handleInputChange} className="w-full h-14 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent" placeholder="Last Name" data-id="element-2090" />
                      </div>
                    </div>

                    <div data-id="element-2091">
                      <label htmlFor="email" className="block text-sm font-bold text-gray-700 mb-1" data-id="element-2092">
                        Email Address
                      </label>
                      <input type="email" id="email" name="email" required value={formData.email} onChange={handleInputChange} className="w-full h-14 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent" placeholder="email@example.com" data-id="element-2093" />
                    </div>

                    <div data-id="element-2094">
                      <label htmlFor="phone_1" className="block text-sm font-bold text-gray-700 mb-1" data-id="element-2095">
                        Phone Number
                      </label>
                      <input type="tel" id="phone_1" name="phone_1" required value={formData.phone_1} onChange={handlePhoneChange} className="w-full h-14 px-4 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent" placeholder="0000000000" maxLength={10} data-id="element-2096" />
                      <p className="text-xs text-gray-500 mt-1" data-id="element-2097">
                        Format: 10 digits only
                      </p>
                    </div>

                    <div className="pt-4" data-id="element-2098">
                      <label className="flex items-start gap-3 mb-4 cursor-pointer" data-id="element-2099">
                        <input type="checkbox" checked={consentChecked} onChange={e => setConsentChecked(e.target.checked)} className="mt-0.5 w-5 h-5 rounded border-gray-300 text-black focus:ring-black cursor-pointer flex-shrink-0" required data-id="element-2100" />
                        <span className="text-[11px] text-gray-500 leading-tight" data-id="element-2101">
                          By clicking the button, I provide my electronic
                          signature and express written consent to share my
                          contact information with KOHLER, including their
                          contractors and affiliates. I authorize them to email,
                          call, or text me for marketing purposes using
                          autodialer technology, AI generated or AI assisted
                          voice, or prerecorded voice messages on the phone
                          number I provided, even if listed on a Do Not Call
                          list. Consent is not a condition to purchase.
                        </span>
                      </label>
                      <button type="submit" disabled={isSubmitting || formData.phone_1.length < 10 || !consentChecked} className="w-full h-16 text-black font-bold text-xl rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed shadow-lg" style={{
                    backgroundColor: brandColor
                  }} data-id="element-2102">
                        {isSubmitting ? 'Processing...' : 'Get Your Free Quote'}
                      </button>
                    </div>
                  </form>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>
      </main>
    </div>;
}